import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { formatCreditBalance } from '@/lib/formatCredits';
import { referralText } from '@/lib/referralSharing';

const styles = StyleSheet.create({
  section: { gap: 18 },
  heading: { gap: 8 },
  title: { fontSize: 24, lineHeight: 30, letterSpacing: -0.6 },
  row: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  card: { flex: 1, minWidth: 160, borderRadius: 18, borderWidth: 1, padding: 20, gap: 16 },
  reward: { fontSize: 30, lineHeight: 38, letterSpacing: -0.8 },
  track: { height: 4, borderRadius: 2, overflow: 'hidden' },
  status: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  terms: { gap: 8 },
});

export default function ReferralMilestones({ stats }) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const dark = isDarkTheme(theme);
  const ink = dark ? '#F5F5F7' : '#1D1D1F';
  const secondary = dark ? '#A1A1A6' : '#6E6E73';
  const line = dark ? '#38383B' : '#E5E5E7';
  const surface = dark ? '#202022' : '#F5F5F7';
  const program = stats.milestoneBonuses;
  if (!program?.milestones?.length) return null;
  const milestones = program.milestones.filter(item => item.status === 'awarded' || stats.maxReferrals == null || item.referrals <= stats.maxReferrals);
  if (!milestones.length) return null;
  const completed = stats.totalCompleted ?? 0;
  const available = milestones.filter(item => item.status === 'upcoming');
  const remainingBonus = available.reduce((sum, item) => sum + item.credits, 0);

  return (
    <View style={styles.section}>
      <View style={styles.heading}>
        <Text style={[styles.title, { color: ink }]} weight="bold" accessibilityRole="header" accessibilityLevel={2}>{t('settings.referral.milestonesTitle')}</Text>
        <Text size="s" style={{ color: secondary, lineHeight: 21 }}>{t('settings.referral.milestonesDescription')}</Text>
        {!program.enabled && <Text size="xs" style={{ color: secondary }}>{t('settings.referral.milestonesPaused')}</Text>}
      </View>
      <View style={styles.row}>
        {milestones.map(milestone => {
          const awarded = milestone.status === 'awarded';
          const past = milestone.status === 'past';
          const remaining = Math.max(0, milestone.referrals - completed);
          return (
            <View key={milestone.referrals} style={[styles.card, { borderColor: line, backgroundColor: awarded ? surface : 'transparent' }]}>
              <Text size="s" weight="semibold" style={{ color: ink }}>{referralText(t, 'milestoneThreshold', { COUNT: milestone.referrals })}</Text>
              <View style={{ gap: 3 }}>
                <Text weight="bold" style={[styles.reward, { color: ink }]}>+{formatCreditBalance(awarded ? milestone.awardedCredits : milestone.credits)}</Text>
                <Text size="xs" style={{ color: secondary }}>{t('settings.referral.extraCredits')}</Text>
              </View>
              <View style={[styles.track, { backgroundColor: line }]} accessibilityRole="progressbar"
                accessibilityLabel={referralText(t, 'milestoneThreshold', { COUNT: milestone.referrals })}
                accessibilityValue={{ min: 0, max: milestone.referrals, now: Math.min(completed, milestone.referrals) }}
                aria-valuemin={0} aria-valuemax={milestone.referrals} aria-valuenow={Math.min(completed, milestone.referrals)}>
                <View style={{ height: 4, width: `${Math.min(100, completed / milestone.referrals * 100)}%`, backgroundColor: ink }} />
              </View>
              <View style={styles.status}>
                {awarded && <Icon name="check" size={12} color={ink} />}
                <Text size="xs" style={{ color: secondary, flex: 1, lineHeight: 18 }}>
                  {awarded ? t('settings.referral.milestoneAwarded') : past ? t('settings.referral.milestonePast')
                    : referralText(t, remaining === 1 ? 'milestoneOneAway' : 'milestoneManyAway', { COUNT: remaining })}
                </Text>
              </View>
            </View>
          );
        })}
      </View>
      <View style={styles.terms}>
        {program.enabled && remainingBonus > 0 && <Text size="s" weight="semibold" style={{ color: ink }}>
          {referralText(t, 'milestonePotential', { CREDITS: formatCreditBalance(remainingBonus) })}
        </Text>}
        {(stats.milestoneCreditsEarned ?? 0) > 0 && <Text size="s" weight="semibold" style={{ color: ink }}>
          {referralText(t, 'milestoneEarned', { CREDITS: formatCreditBalance(stats.milestoneCreditsEarned) })}
        </Text>}
        <Text size="xs" style={{ color: secondary, lineHeight: 19 }}>{referralText(t, 'milestoneStacking', { CREDITS: formatCreditBalance(stats.creditsPerReferral ?? 250) })}</Text>
        <Text size="xs" style={{ color: secondary, lineHeight: 19 }}>{t('settings.referral.milestoneEligibility')}</Text>
      </View>
    </View>
  );
}
